// app/lib/path-progress.ts
// Leerpadvoortgang in localStorage, als één module-store met
// useSyncExternalStore erbovenop — dezelfde lijn als app/lib/chat-visitor.ts
// ("EEN STORE, GEEN REACT QUERY"; React Query bestaat op de publieke kant niet,
// die provider wordt pas in routes/admin/layout.tsx gemaakt).
//
// VIER DINGEN DIE DE VORIGE VERSIE FOUT DEED:
//
// 1. De sleutel was de SLUG. src/paths.js:97-99 hergenereert de slug zodra een
//    beheerder de titel van een leerpad wijzigt — en dan was de voortgang van
//    iedereen stil weg. Nu staat alles onder het path-id, dat vastligt.
// 2. writePath() werd aangeroepen BINNEN de setState-updater van de pagina.
//    StrictMode roept updaters dubbel aan; een schrijfactie hoort daar niet.
// 3. Twee tabbladen liepen uit de pas: niemand luisterde op `storage`.
// 4. De pagina las na de hydratatie, dus de balk stond één beeld lang op 0%.
//    getServerSnapshot lost dat bij de wortel op: React gebruikt hem óók voor
//    de hydratatierender, dus server-HTML en eerste clientrender zijn gelijk
//    PER CONSTRUCTIE — en na een zachte navigatie is de store al warm, dan is
//    er zelfs helemaal geen tussenstand meer.

import { useCallback, useSyncExternalStore } from 'react';

const KEY = 'kb:path-progress';
/** De allereerste vorm: één localStorage-sleutel PER leerpad. */
const LEGACY_PREFIX = 'kb-path-progress:';
/** Niet-opgeëiste v1-resten verlopen. Een pad dat hernoemd is voordat de lezer
 *  terugkwam, kan nooit meer gekoppeld worden; dan moet het ook niet eeuwig
 *  in de blob blijven staan. */
const LEGACY_TTL = 180 * 24 * 3600_000;

/** Hoe een stap ervoor staat.
 *
 *  0 = de lezer heeft hem ZELF uitgevinkt. Een expliciete weigering, en de
 *      reden dat dit een getal is en geen `delete`: zonder dit grafsteentje
 *      vinkt de automaat de stap vijftien seconden later gewoon weer aan.
 *  1 = met de hand afgevinkt.
 *  2 = automatisch afgevinkt, omdat de lezer het artikel uitlas.
 *  afwezig = nooit aangeraakt.
 *
 *  Getallen en geen booleans zodat `!!mark` "afgerond" blijft betekenen: 0 is
 *  falsy én bewaard, precies wat een grafsteentje nodig heeft. */
export type StepMark = 0 | 1 | 2;

export interface PathEntry {
  /** articleId → StepMark. Op articleId en niet op index: herordenen in het
   *  beheer mag voortgang niet laten verschuiven. */
  steps: Record<string, StepMark>;
  /** articleId van de laatst geopende stap — voedt "Ga verder met stap 4".
   *  Geen index, want die verschuift bij herordenen. */
  cursor?: string;
  /** ms sinds epoch. */
  at?: number;
}

interface Db {
  v: 2;
  /** Op leerpad-id (de UUID uit data/paths.json). */
  paths: Record<string, PathEntry>;
  /** v1-gegevens die nog niet aan een id gekoppeld konden worden, op slug. */
  legacy?: Record<string, { steps: Record<string, StepMark>; at: number }>;
  settings?: { auto?: boolean };
}

/** MOET een stabiele identiteit hebben, anders klaagt React met "The result of
 *  getServerSnapshot should be cached" — zelfde reden als SSR_SNAPSHOT in
 *  chat-visitor.ts. */
const EMPTY: Db = Object.freeze({ v: 2, paths: Object.freeze({}) }) as Db;
const NO_ENTRY: PathEntry = Object.freeze({ steps: Object.freeze({}) }) as PathEntry;

let db: Db | null = null;
/** Privémodus of een vol quotum. De store draait dan verder in het geheugen,
 *  zodat de sessie blijft werken in plaats van halverwege stil te vallen. */
let persistFailed = false;
const listeners = new Set<() => void>();

/* ------------------------------------------------------------------ *
 *  Lezen en migreren
 * ------------------------------------------------------------------ */

/** v1 sloeg `true` op. Alles wat we in de oude vorm vinden is per definitie
 *  met de hand aangevinkt — nooit 2, anders labelt de lijst een klik van
 *  vorige maand achteraf als "automatisch afgevinkt". */
function toMarks(raw: unknown): Record<string, StepMark> {
  const out: Record<string, StepMark> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v === 2) out[id] = 2;
    else if (v === 0) out[id] = 0;
    else if (v) out[id] = 1;
  }
  return out;
}

function migrate(raw: unknown, now: number): Db {
  if (!raw || typeof raw !== 'object') return { v: 2, paths: {} };
  const o = raw as Record<string, unknown>;
  if (o.v === 2) {
    return {
      v: 2,
      paths: (o.paths as Record<string, PathEntry>) || {},
      legacy: o.legacy as Db['legacy'],
      settings: o.settings as Db['settings'],
    };
  }
  // v1: { [slug]: { [articleId]: true } }. Gaat ONGEWIJZIGD naar `legacy` —
  // we kennen de id's nog niet, dus er wordt niets weggegooid.
  const legacy: NonNullable<Db['legacy']> = {};
  for (const [slug, steps] of Object.entries(o)) {
    const marks = toMarks(steps);
    if (Object.keys(marks).length) legacy[slug] = { steps: marks, at: now };
  }
  return Object.keys(legacy).length ? { v: 2, paths: {}, legacy } : { v: 2, paths: {} };
}

function load(): Db {
  const now = Date.now();
  let next: Db;
  try {
    const raw = localStorage.getItem(KEY);
    next = migrate(raw ? JSON.parse(raw) : null, now);
  } catch {
    persistFailed = true;
    return { v: 2, paths: {} };
  }

  // v0 → de N losse sleutels opzuigen. Eenmalig; ze gaan er meteen af.
  let dirty = false;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k?.startsWith(LEGACY_PREFIX)) continue;
      const slug = k.slice(LEGACY_PREFIX.length);
      try {
        const marks = toMarks(JSON.parse(localStorage.getItem(k) || '{}'));
        if (Object.keys(marks).length) {
          next.legacy = next.legacy || {};
          next.legacy[slug] = { steps: { ...marks, ...(next.legacy[slug]?.steps || {}) }, at: now };
        }
      } catch { /* stuk, weg ermee */ }
      localStorage.removeItem(k);
      dirty = true;
    }
  } catch { /* niet fataal */ }

  if (dirty) write(next);
  return next;
}

function write(next: Db) {
  // Verlopen resten opruimen: die zijn niet meer te koppelen.
  if (next.legacy) {
    const now = Date.now();
    const keep: NonNullable<Db['legacy']> = {};
    for (const [slug, v] of Object.entries(next.legacy)) {
      if (now - (v.at || 0) < LEGACY_TTL) keep[slug] = v;
    }
    next.legacy = Object.keys(keep).length ? keep : undefined;
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
    persistFailed = false;
  } catch {
    // Nog één poging zonder de resten; daarna eerlijk zijn dat het niet lukt.
    try {
      localStorage.setItem(KEY, JSON.stringify({ ...next, legacy: undefined }));
      next.legacy = undefined;
      persistFailed = false;
    } catch {
      persistFailed = true;
    }
  }
}

function get(): Db {
  if (typeof window === 'undefined') return EMPTY;
  if (!db) db = load();
  return db;
}

function commit(next: Db) {
  db = next;
  write(next);
  for (const l of listeners) l();
}

/* ------------------------------------------------------------------ *
 *  Abonneren
 * ------------------------------------------------------------------ */

function onStorage(e: StorageEvent) {
  // e.key === null betekent dat een ander tabblad alles wiste.
  if (e.key !== null && e.key !== KEY) return;
  db = null;
  for (const l of listeners) l();
}

/** Terug uit de bfcache: een ander tabblad kan intussen van alles hebben
 *  geschreven zonder dat deze bevroren pagina een storage-event zag. */
function onPageShow(e: PageTransitionEvent) {
  if (!e.persisted) return;
  db = null;
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  if (listeners.size === 1) {
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
  }
  return () => {
    listeners.delete(cb);
    if (listeners.size === 0) {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', onPageShow);
    }
  };
}

const getSnapshot = () => get();
/** React gebruikt deze óók voor de hydratatierender — vandaar dat de server
 *  nooit een voortgangsgetal toont dat een tel later verspringt. */
const getServerSnapshot = () => EMPTY;

/* ------------------------------------------------------------------ *
 *  Schrijven
 * ------------------------------------------------------------------ */

function put(pathId: string, entry: PathEntry | null) {
  const cur = get();
  const paths = { ...cur.paths };
  // LEEG betekent: geen markeringen én geen cursor. Een entry met alleen een
  // 0 erin is NIET leeg — weggooien zou de weigering wissen en de automaat
  // weer loslaten op precies de stap die de lezer uitvinkte.
  if (!entry || (Object.keys(entry.steps).length === 0 && !entry.cursor)) delete paths[pathId];
  else paths[pathId] = entry;
  commit({ ...cur, paths });
}

const entryOf = (pathId: string): PathEntry => get().paths[pathId] || NO_ENTRY;

/** Zet een stap op een markering. */
export function setMark(pathId: string, articleId: string, mark: StepMark) {
  const cur = entryOf(pathId);
  if (cur.steps[articleId] === mark) return;
  put(pathId, { ...cur, steps: { ...cur.steps, [articleId]: mark }, at: Date.now() });
}

/** Aan/uit vanuit de interface. Uitvinken zet 0 (een weigering), niet
 *  "afwezig" — zie StepMark. */
export function toggleStep(pathId: string, articleId: string, done: boolean) {
  setMark(pathId, articleId, done ? 1 : 0);
}

/** Vinkt automatisch af, maar ALLEEN als de stap nog nergens op staat.
 *  Geeft terug of het echt gebeurd is; alleen dán mag de bevestiging in beeld.
 *  Idempotent: een tweede aanroep levert false. */
export function autoComplete(pathId: string, articleId: string): boolean {
  if (!autoEnabled()) return false;
  if (entryOf(pathId).steps[articleId] !== undefined) return false;
  setMark(pathId, articleId, 2);
  return true;
}

/** Onthoudt welke stap als laatste geopend werd. */
export function touchStep(pathId: string, articleId: string) {
  const cur = entryOf(pathId);
  if (cur.cursor === articleId) return;
  put(pathId, { ...cur, cursor: articleId, at: Date.now() });
}

export function resetPath(pathId: string) {
  put(pathId, null);
}

export function autoEnabled(): boolean {
  return get().settings?.auto !== false;
}

export function setAutoEnabled(on: boolean) {
  const cur = get();
  commit({ ...cur, settings: { ...cur.settings, auto: on } });
}

/** Koppelt v1-voortgang (op slug) aan het stabiele path-id.
 *
 *  /leerpaden geeft alle paren in één keer door, dus één bezoek aan het
 *  overzicht zet de hele voorraad over. Bij een botsing wint 0 (een weigering
 *  van ná de migratie) en anders wat er al onder het id staat. */
export function claim(pairs: { id: string; slug: string }[]) {
  if (typeof window === 'undefined') return;
  const cur = get();
  if (!cur.legacy) return;

  const rest = { ...cur.legacy };
  const paths = { ...cur.paths };
  let changed = false;

  for (const { id, slug } of pairs) {
    const old = rest[slug];
    if (!old) continue;
    delete rest[slug];
    changed = true;
    const existing = paths[id];
    if (!existing) {
      paths[id] = { steps: old.steps, at: old.at };
      continue;
    }
    const steps = { ...old.steps };
    for (const [articleId, mark] of Object.entries(existing.steps)) {
      steps[articleId] = mark === 0 ? 0 : mark;
    }
    paths[id] = { ...existing, steps };
  }

  if (!changed) return;
  commit({ ...cur, paths, legacy: Object.keys(rest).length ? rest : undefined });
}

/* ------------------------------------------------------------------ *
 *  Pure helpers — hier hangt de interface aan, en de tests
 * ------------------------------------------------------------------ */

/** Telt alleen stappen die NU nog in het pad zitten.
 *
 *  Zonder die doorsnede telt /leerpaden ook markeringen van stappen die
 *  intussen uit het pad gehaald zijn, en dan staat er "12 / 11 afgerond". */
export function countDone(entry: PathEntry, stepIds: string[]): number {
  let n = 0;
  for (const id of stepIds) if (entry.steps[id]) n++;
  return n;
}

/** Waar moet "Ga verder" heen?
 *   1. de cursor, als die nog in het pad zit én nog niet afgerond is;
 *   2. anders de eerste stap die niet afgerond is (0 telt als niet afgerond);
 *   3. anders null — het pad is uit. */
export function resumeStep(
  entry: PathEntry,
  stepIds: string[],
): { articleId: string; index: number } | null {
  const { cursor } = entry;
  if (cursor && !entry.steps[cursor]) {
    const i = stepIds.indexOf(cursor);
    if (i !== -1) return { articleId: cursor, index: i };
  }
  const i = stepIds.findIndex((id) => !entry.steps[id]);
  return i === -1 ? null : { articleId: stepIds[i]!, index: i };
}

/* ------------------------------------------------------------------ *
 *  Hooks
 * ------------------------------------------------------------------ */

/** Staat de client al? Voor alles wat pas ná de hydratatie mag verschijnen
 *  omdat het uit localStorage komt. */
export function useHydrated(): boolean {
  return useSyncExternalStore(subscribe, () => true, () => false);
}

export function usePathProgress(pathId: string) {
  const store = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const ready = useHydrated();
  const entry = store.paths[pathId] || NO_ENTRY;

  const markOf = useCallback((articleId: string) => entry.steps[articleId], [entry]);
  const isDone = useCallback((articleId: string) => Boolean(entry.steps[articleId]), [entry]);
  const toggle = useCallback(
    (articleId: string, done: boolean) => toggleStep(pathId, articleId, done),
    [pathId],
  );
  const touch = useCallback((articleId: string) => touchStep(pathId, articleId), [pathId]);
  const reset = useCallback(() => resetPath(pathId), [pathId]);

  return { entry, markOf, isDone, toggle, touch, reset, ready, persistFailed };
}

/** Alle paden tegelijk — voor het overzicht op /leerpaden. */
export function useAllProgress() {
  const store = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return store.paths;
}

/** De aan/uit-schakelaar voor automatisch afvinken, als geabonneerde waarde —
 *  zodat het vinkje meebeweegt als een ander tabblad hem omzet. */
export function useAutoEnabled(): boolean {
  const store = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return store.settings?.auto !== false;
}

/* Alleen voor de tests. */
export const __test = {
  reset() {
    db = null;
    persistFailed = false;
    listeners.clear();
  },
  onStorage,
  get persistFailed() { return persistFailed; },
};
